import React, { useEffect, useRef, useState } from 'react';
import { FaTimes, FaSpinner, FaExclamationTriangle } from 'react-icons/fa';
import meetingService from '../../services/meetingService';
import '../FileManager/OnlyOfficeViewerModal.css';

const SCRIPT_ID = 'onlyoffice-docs-api';

const detectDocumentType = (ext) => {
    const e = (ext || '').toLowerCase();
    if (['doc', 'docx', 'odt', 'rtf', 'txt'].includes(e)) return 'word';
    if (['xls', 'xlsx', 'ods', 'csv'].includes(e))         return 'cell';
    if (['ppt', 'pptx', 'odp'].includes(e))                return 'slide';
    return null;
};

const MeetingOnlyOfficeViewerModal = ({ attachment, meetingId, onClose }) => {
    const editorRef       = useRef(null);
    const containerRef    = useRef(null);
    const wasModifiedRef  = useRef(false);
    const destroyTimerRef = useRef(null);
    const [status, setStatus]     = useState('loading');
    const [errorMsg, setErrorMsg] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (!attachment) return;

        setStatus('loading');
        setErrorMsg('');

        let cancelled = false;
        let docEditor  = null;

        const cleanup = () => {
            cancelled = true;
            if (destroyTimerRef.current) clearTimeout(destroyTimerRef.current);
            if (docEditor) {
                try {
                    if (wasModifiedRef.current) docEditor.forceSave();
                } catch (_) {}
                const editorToDestroy = docEditor;
                docEditor = null;
                destroyTimerRef.current = setTimeout(() => {
                    try { editorToDestroy.destroyEditor(); } catch (_) {}
                }, wasModifiedRef.current ? 2000 : 0);
            }
            const existingScript = document.getElementById(SCRIPT_ID);
            if (existingScript) existingScript.remove();
        };

        const init = async () => {
            try {
                // 1. Fetch config from meeting-specific endpoint
                const res = await meetingService.getAttachmentOnlyOfficeConfig(meetingId, attachment.id);
                if (cancelled) return;

                if (!res?.status) {
                    setErrorMsg('Failed to load document configuration.');
                    setStatus('error');
                    return;
                }

                const config  = res.data;
                const docType = detectDocumentType(config.fileType);
                if (!docType) {
                    setErrorMsg(`Unsupported file type: .${config.fileType}`);
                    setStatus('error');
                    return;
                }

                // 2. Load DocsAPI script from OnlyOffice server
                const serverUrl = process.env.REACT_APP_ONLYOFFICE_SERVER_URL || 'http://localhost:8081';
                const scriptSrc = `${serverUrl}/web-apps/apps/api/documents/api.js`;

                await new Promise((resolve, reject) => {
                    if (document.getElementById(SCRIPT_ID)) { resolve(); return; }
                    const script    = document.createElement('script');
                    script.id       = SCRIPT_ID;
                    script.src      = scriptSrc;
                    script.onload   = resolve;
                    script.onerror  = () => reject(new Error('Failed to load OnlyOffice DocsAPI script. Is the server running?'));
                    document.head.appendChild(script);
                });

                if (cancelled) return;
                if (!window.DocsAPI) throw new Error('DocsAPI not available after script load.');

                // 3. Initialise the editor
                const isOwner    = config.permissions?.is_owner || false;
                const editorMode = isOwner ? 'edit' : 'view';
                docEditor = new window.DocsAPI.DocEditor('onlyoffice-editor-container', {
                    document: {
                        fileType   : config.fileType,
                        key        : config.key,
                        title      : config.title,
                        url        : config.documentUrl,
                        permissions: { edit: isOwner, download: true },
                    },
                    editorConfig: {
                        mode: editorMode,
                        ...(isOwner && config.callbackUrl && { callbackUrl: config.callbackUrl }),
                        customization: {
                            autosave  : isOwner && !!config.callbackUrl,
                            forcesave : isOwner && !!config.callbackUrl,
                            hideRightMenu: true,
                            features: { tabStyle: 'line', tabBackground: 'white' },
                        },
                    },
                    documentType: docType,
                    height: '100%',
                    width : '100%',
                    events: {
                        onAppReady           : () => { if (!cancelled) setStatus('ready'); },
                        onDocumentStateChange: (event) => { if (event?.data === true) wasModifiedRef.current = true; },
                        onRequestSaveAs      : () => { if (!cancelled) setIsSaving(true); },
                        onSave               : () => { if (!cancelled) setIsSaving(false); },
                        onError              : (event) => {
                            if (!cancelled) {
                                setIsSaving(false);
                                setErrorMsg(event?.data?.errorDescription || 'An error occurred in the document editor.');
                                setStatus('error');
                            }
                        },
                    },
                });

                editorRef.current = docEditor;
            } catch (err) {
                if (!cancelled) {
                    setErrorMsg(err.message || 'Unexpected error loading document.');
                    setStatus('error');
                }
            }
        };

        init();
        return cleanup;
    }, [attachment, meetingId]);

    const handleClose = () => {
        if (isSaving) return;
        onClose(wasModifiedRef.current);
    };

    useEffect(() => {
        if (!attachment) return;
        const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [attachment, onClose]);

    if (!attachment) return null;

    const title = attachment.file_name || 'Document';

    return (
        <div
            className="oo-overlay"
            onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
            role="dialog"
            aria-modal="true"
            aria-label={`Viewing ${title}`}
        >
            <div className="oo-modal">
                <div className="oo-header">
                    <span className="oo-title" title={title}>{title}</span>
                    {isSaving && (
                        <span className="oo-saving-indicator" aria-live="polite">Saving…</span>
                    )}
                    <button
                        id="onlyoffice-close-btn"
                        className="oo-close-btn"
                        onClick={handleClose}
                        disabled={isSaving}
                        aria-label="Close document viewer"
                        title={isSaving ? 'Saving changes, please wait…' : 'Close'}
                    >
                        <FaTimes />
                    </button>
                </div>

                <div className="oo-body" ref={containerRef}>
                    <div
                        id="onlyoffice-editor-container"
                        className="oo-editor-container"
                        style={{ visibility: status === 'ready' ? 'visible' : 'hidden' }}
                    />
                    {status === 'loading' && (
                        <div className="oo-state-overlay">
                            <FaSpinner className="oo-spinner" />
                            <p>Loading document…</p>
                        </div>
                    )}
                    {status === 'error' && (
                        <div className="oo-state-overlay oo-error">
                            <FaExclamationTriangle className="oo-error-icon" />
                            <p className="oo-error-msg">{errorMsg}</p>
                            <button className="oo-retry-btn" onClick={handleClose}>Close</button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MeetingOnlyOfficeViewerModal;
